import { ComponentProps, useEffect, useState } from "react";

import { Heading } from "@components/common/ui/Heading";
import { Image } from "@components/common/ui/Image";

import EbookImageDark from "public/assets/images/default-images/ebook-default-dark.svg";
import EbookImageLight from "public/assets/images/default-images/ebook-default-light.svg";
import ServiceImageDark from "public/assets/images/default-images/service-default-dark.svg";
import ServiceImageLight from "public/assets/images/default-images/service-default-light.svg";
import VideoclassImageDark from "public/assets/images/default-images/videoclass-default-dark.svg";
import VideoclassImageLight from "public/assets/images/default-images/videoclass-default-light.svg";

import { Skeleton } from "@kirvanobr/skeleton";
import * as S from "./styles";
import { Edit02 } from "@untitled-ui/icons-react";

interface ProductImageProps extends ComponentProps<typeof S.Container> {
  format?: string;
  photoURL?: string | null;
  fullWidth?: boolean;
  isLoading?: boolean;
}

interface DefaultImageProps {
  light: string;
  dark: string;
}

export default function ProductImage({
  format,
  photoURL,
  variant = "large",
  fullWidth = false,
  isLoading = false,
  ...props
}: ProductImageProps) {
  const defaultImage = (format?: string): DefaultImageProps => {
    switch (format) {
      case "videoclass":
      case "video-aula":
      case "Vídeo aula":
      case "community":
      case "comunidade":
      case "Comunidade":
      case "Curso":
        return { light: VideoclassImageLight, dark: VideoclassImageDark };

      case "service":
      case "servicos":
      case "Serviço":
      case "Serviços":
        return { light: ServiceImageLight, dark: ServiceImageDark };

      default:
        return { light: EbookImageLight, dark: EbookImageDark };
    }
  };

  const [imgSrc, setImgSrc] = useState<DefaultImageProps>(defaultImage(format));

  useEffect(() => {
    if (!photoURL) return setImgSrc(defaultImage(format));
    setImgSrc({ light: photoURL, dark: photoURL });
  }, [photoURL, format]);

  if (isLoading)
    return (
      <S.Container fullWidth={fullWidth} variant={variant} {...props}>
        <Skeleton height={"100%"} />
      </S.Container>
    );

  return (
    <S.Container fullWidth={fullWidth} variant={variant} {...props}>
      <Image
        lightIcon={imgSrc.light || defaultImage(format).light}
        darkIcon={imgSrc.dark || defaultImage(format).dark}
        alt="Product cover"
        fill
        sizes="100%"
        priority
        onError={() => {
          setImgSrc(defaultImage(format));
        }}
      />

      {!!props.onClick && (
        <span className={"hover-content"}>
          {variant === "small" && (
            <S.EditImageHoverSmall className="edit-image">
              <Edit02 fontSize={15} />
            </S.EditImageHoverSmall>
          )}

          {variant === "large" && (
            <>
              <S.EditImageHoverLarge className="edit-image">
                <Edit02 fontSize={16} /> Alterar imagem
              </S.EditImageHoverLarge>

              <S.EditImageHoverLarge className="edit-image" bottom description>
                <Heading size={"xx-small"} weight={"medium"} variant={"white"} className="edit-image-text">
                  Aceitamos os formatos .jpg e .png com menos de 500kb. Sugestão de tamanho: 730 X 240px
                </Heading>
              </S.EditImageHoverLarge>
            </>
          )}
        </span>
      )}
    </S.Container>
  );
}
